<template>
  <div class="InputIp">
    <div class="input-group-container">
      <div class="field-group">
        <label>{{ t('unit.ip_address') }}:</label>
        <IpAddressInput
          v-model="localIp"
          :invalid="ipState === false || (!isValidIp && localIp !== '') || (showErrors && required && isIpEmpty)"
        />
        <small v-if="showErrors && required && isIpEmpty" class="error-msg"
          >{{ t('unit.ip_required') }}</small
        >
        <small v-else-if="!isValidIp && localIp !== ''" class="error-msg"
          >{{ t('unit.invalid_ip_format') }}</small
        >
      </div>

      <div class="field-group">
        <label>{{ t('unit.subnet_mask') }}:</label>
        <IpAddressInput v-model="localMask" :invalid="(!isValidMask && localMask !== '') || (showErrors && required && isMaskEmpty)" />
        <small v-if="showErrors && required && isMaskEmpty" class="error-msg"
          >{{ t('unit.mask_required') }}</small
        >
        <small v-else-if="!isValidMask && localMask !== ''" class="error-msg"
          >{{ t('unit.invalid_range') }}</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "@vue/composition-api";
import IpAddressInput from "./IpAddressInput.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  name: "InputIp",
  components: { IpAddressInput },
  props: {
    value: {
      type: String,
      default: "",
    },
    mask: {
      type: String,
      default: "255.255.255.0",
    },
    ipState: {
      type: Boolean,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    showErrors: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const localIp = ref(props.value);
    const localMask = ref(props.mask);

    watch(
      () => props.value,
      (val) => {
        if (val !== localIp.value) localIp.value = val;
      }
    );
    watch(
      () => props.mask,
      (val) => {
        if (val !== localMask.value) localMask.value = val;
      }
    );

    watch(localIp, (val) => emit("input", val));
    watch(localMask, (val) => emit("update:mask", val));

    // --- Validaciones ---
    const ipPattern =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

    const isValidIp = computed(() => {
      return ipPattern.test(localIp.value);
    });

    const isIpEmpty = computed(() => {
      return !localIp.value || localIp.value === '' || localIp.value === '...';
    });

    const isMaskEmpty = computed(() => {
      return !localMask.value || localMask.value === '' || localMask.value === '...';
    });

    const ipToLong = (ip) => {
      return (
        ip.split(".").reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0
      );
    };

    const isValidMask = computed(() => {
      if (!ipPattern.test(localMask.value)) return false;
      const long = ipToLong(localMask.value);
      const bin = (long >>> 0).toString(2).padStart(32, "0");
      return /^1*0*$/.test(bin);
    });

    const isComponentValid = computed(() => {
      if (props.required) {
        return isValidIp.value && isValidMask.value && !isIpEmpty.value && !isMaskEmpty.value;
      }
      // Si no es requerido, solo validar formato si hay valor
      const ipValid = isIpEmpty.value || isValidIp.value;
      const maskValid = isMaskEmpty.value || isValidMask.value;
      return ipValid && maskValid;
    });

    watch(
      isComponentValid,
      (val) => {
        emit("on-validation", val);
      },
      { immediate: true }
    );

    return {
      t,
      localIp,
      localMask,
      isValidIp,
      isValidMask,
      isIpEmpty,
      isMaskEmpty,
    };
  },
};
</script>

<style scoped>
.input-group-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.field-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

label {
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  font-weight: bold;
}

.error-msg {
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.status-box {
  margin-top: 1rem;
  padding: 1rem;
  background: #e8f4fd;
  border-left: 5px solid #3498db;
  border-radius: 4px;
}

.highlight {
  font-weight: bold;
  color: #2980b9;
}
</style>
